// Pagina publica do produto /catalogo/:id — sem login.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Check } from 'lucide-react';
import { extrairIdDoSlug } from '@fiodecobre/shared';
import { RodapeCatalogo } from './RodapeCatalogo';
import { AviseMeQuandoChegar } from './AviseMeQuandoChegar';
import { pingWhatsapp } from './metrica';

type Produto = {
  id: number;
  codigo_interno: string | null;
  descricao_comercial: string;
  vantagens: string | null;
  preco_pix: string;
  foto_url: string;
  disponivel: boolean;
  grupo_id: number | null;
  grupo_nome: string | null;
};

function formatBRL(v: string | number): string {
  return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseVantagens(s: string | null): string[] {
  if (!s) return [];
  return s
    .split(/\r?\n/)
    .map((l) => l.trim().replace(/^[-*•]\s*/, ''))
    .filter((l) => l.length > 0);
}

export function CatalogoPublicoProduto() {
  const { slugId } = useParams();
  const navigate = useNavigate();
  const id = extrairIdDoSlug(slugId);
  const [produto, setProduto] = useState<Produto | null>(null);
  const [config, setConfig] = useState<{ whatsapp_link_base: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setErro('URL invalida.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setErro(null);
    fetch(`/api/public/catalogo/${id}`)
      .then(async (r) => {
        if (!r.ok) {
          setErro(r.status === 404 ? 'Produto nao encontrado.' : 'Erro ao carregar produto.');
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) {
          setProduto(data);
          document.title = `${data.descricao_comercial} — FIODECOBRE`;
          // Normaliza URL pra canonica curta /p/<id>. Cobre 3 casos:
          // 1) usuario entrou via /catalogo/<slugId> (apesar do 301 do server)
          // 2) usuario entrou via /p/<slug-id> (com slug ornamental)
          // 3) hash/qualquer outra variacao
          if (window.location.pathname !== `/p/${data.id}`) {
            navigate(`/p/${data.id}`, { replace: true });
          }
        }
      })
      .catch(() => setErro('Erro de conexao.'))
      .finally(() => setLoading(false));

    fetch('/api/public/catalogo/config').then((r) => r.json()).then(setConfig).catch(() => setConfig(null));
  }, [id, slugId, navigate]);

  const vantagens = parseVantagens(produto?.vantagens ?? null);

  const linkWhatsapp =
    config?.whatsapp_link_base && produto
      ? `${config.whatsapp_link_base}?text=${encodeURIComponent(
          `Olá! Tenho interesse no produto: ${produto.descricao_comercial}` +
            (produto.codigo_interno ? ` (cod ${produto.codigo_interno})` : '') +
            `. Pode me ajudar?`,
        )}`
      : null;

  return (
    <div className="catalogo-publico-root" style={{ minHeight: '100vh', backgroundColor: '#f7f7f7', color: '#111', paddingBottom: '96px' }}>
      {/* Header simples com voltar */}
      <header style={{ backgroundColor: '#b45309', color: '#fff' }} className="sticky top-0 z-30 shadow">
        <div className="max-w-3xl mx-auto px-3 py-3 flex items-center gap-3">
          <Link
            to="/catalogo"
            className="inline-flex items-center justify-center rounded-full"
            style={{ width: 36, height: 36, backgroundColor: 'rgba(255,255,255,0.15)' }}
          >
            <ArrowLeft size={18} />
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-medium opacity-90 truncate">Fio de Cobre</h1>
            <p className="text-xs opacity-75 truncate">{produto?.grupo_nome ?? 'Catalogo'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-3 py-3">
        {loading ? (
          <div className="text-center text-gray-500 py-12">Carregando...</div>
        ) : erro ? (
          <div className="text-center text-gray-500 py-12">
            {erro}
            <div className="mt-4">
              <Link to="/catalogo" className="underline" style={{ color: '#b45309' }}>
                Voltar para o catalogo
              </Link>
            </div>
          </div>
        ) : produto ? (
          <>
          {/* Card unico: foto + info + caracteristicas. Foto com maxHeight pra caber tudo no fold. */}
          <div className="rounded-lg overflow-hidden shadow-sm border border-gray-200" style={{ backgroundColor: '#fff' }}>
            {/* Foto compacta — max 260px de altura no mobile pra titulo+preco+caracteristicas aparecerem sem rolar */}
            <div className="bg-gray-100 relative flex items-center justify-center" style={{ height: '40vh', maxHeight: 320, minHeight: 200 }}>
              <img
                src={produto.foto_url}
                alt={produto.descricao_comercial}
                className="max-w-full max-h-full object-contain"
                style={!produto.disponivel ? { filter: 'grayscale(1)', opacity: 0.55 } : undefined}
              />
              {!produto.disponivel && (
                <span
                  className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold rounded uppercase tracking-wide"
                  style={{ backgroundColor: '#374151', color: '#fff' }}
                >
                  Esgotado
                </span>
              )}
            </div>

            {/* Info — sem card separado, segue dentro do mesmo */}
            <div className="px-4 pt-3 pb-2 border-t border-gray-100">
              <h2 className="text-base sm:text-lg font-semibold leading-tight">{produto.descricao_comercial}</h2>
              {produto.codigo_interno && (
                <p className="text-[11px] text-gray-500 mt-0.5">Cod {produto.codigo_interno}</p>
              )}
              <div className="mt-1.5 flex items-baseline gap-2 flex-wrap">
                {produto.disponivel ? (
                  <>
                    <div className="text-2xl font-bold" style={{ color: '#b45309' }}>
                      R$ {formatBRL(produto.preco_pix)}
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">No Pix ou dinheiro</div>
                  </>
                ) : (
                  <div className="text-lg font-semibold text-gray-500">Indisponivel no momento</div>
                )}
              </div>
            </div>

            {/* Vantagens — sem header chamativo, lista enxuta dentro do mesmo card */}
            {vantagens.length > 0 && (
              <div className="px-4 pb-3 pt-2 border-t border-gray-100">
                <ul className="space-y-1">
                  {vantagens.map((v, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[13px] leading-snug">
                      <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#b45309' }} />
                      <span>{v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {!produto.disponivel && (
            <div className="mt-3">
              <AviseMeQuandoChegar produtoId={produto.id} />
            </div>
          )}
          </>
        ) : null}
      </main>

      <RodapeCatalogo />

      {/* Botao WhatsApp fixed no bottom */}
      {linkWhatsapp && produto && (
        <div
          className="fixed bottom-0 left-0 right-0 px-3 py-3"
          style={{ backgroundColor: 'rgba(247,247,247,0.95)', borderTop: '1px solid #e5e5e5', backdropFilter: 'blur(4px)' }}
        >
          <div className="max-w-3xl mx-auto">
            <a
              href={linkWhatsapp}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => pingWhatsapp(produto.id)}
              className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-base shadow"
              style={{ backgroundColor: '#25d366', color: '#fff', textDecoration: 'none' }}
            >
              <MessageCircle size={20} /> Falar pelo WhatsApp
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
